/**
 * Fetches the Realm-provided country of residence for pre-filling the login
 * modal's Country picker. This is the ONE piece of the flow that needs the slow
 * Minelab UserDetails service, so it's split out of member-status (which is
 * DB-only and decides Welcome-Back-vs-Join instantly). The modal opens off
 * member-status and pre-fills Country when THIS resolves — a field-level skeleton
 * loader, never a blocking wait.
 *
 * Only fires when a partner handshake is present (`rttrStore.realmUuid/Ts/Sig`)
 * AND `enabled` — the caller gates it on "country pre-fill is actually needed"
 * (a not-yet-registered visitor / no country on file), so a returning member
 * with country on file never triggers the slow call.
 *
 * Backend is `GET /challenges/:slug/realm-country` (no auth — the handshake HMAC
 * is the auth proof; headers attached by `getConfig`). Country only; the Realm
 * email is never exposed to the client.
 */
import {useQuery} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {ChallengeRealmCountryResponse, ChallengesApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {IStore} from "@/redux/defaultStore";

export function useGetChallengeRealmCountry(slug: string | undefined, enabled: boolean) {
    const realmUuid = useSelector((store: IStore) => store.rttrStore?.realmUuid);
    const realmTs = useSelector((store: IStore) => store.rttrStore?.realmTs);
    const realmSig = useSelector((store: IStore) => store.rttrStore?.realmSig);

    const hasHandoff = Boolean(realmUuid && realmTs && realmSig);

    return useQuery({
        /*
         * Include `realmTs` in the key so a fresh handshake with the same UUID but
         * a new timestamp forces a fresh fetch — without it React Query would serve
         * the stale 60s cache entry on a re-embed even when Realm-side state changed.
         */
        queryKey: ["challenge-realm-country", slug, realmUuid ?? "no-handoff", realmTs ?? "no-ts"],
        queryFn: async (): Promise<ChallengeRealmCountryResponse> => {
            const api = new ChallengesApi(getConfig());
            /*
             * The generated client requires the three handshake headers as named
             * arguments (swagger declares them in:header, required:true). `getConfig`
             * ALSO attaches them globally via Configuration headers — that's the
             * authoritative source; these named args just satisfy the generated
             * signature. Passing the same Redux values keeps the two in sync with no
             * config-vs-args drift surface. (Mirrors useGetChallengeMemberStatus.)
             */
            return api.getChallengesSlugRealmCountry({
                slug: slug!,
                xRealmUUID: realmUuid!,
                xRealmTS: realmTs!,
                xRealmSig: realmSig!,
            });
        },
        enabled: Boolean(slug) && hasHandoff && enabled,
        staleTime: 60_000,
    });
}
